import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

interface FeatureFlagContextType {
  kamariMode: boolean
  toggleKamariMode: () => void
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  kamariMode: false,
  toggleKamariMode: () => {},
})

export function useFeatureFlags() {
  return useContext(FeatureFlagContext)
}

const STORAGE_KEY = 'yi-kamari-mode'

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams()

  const [kamariMode, setKamariMode] = useState(() => {
    // Check URL param first, then localStorage
    if (typeof window !== 'undefined') {
      const urlParam = new URLSearchParams(window.location.search).get('kamari')
      if (urlParam === 'true') return true
      return localStorage.getItem(STORAGE_KEY) === 'true'
    }
    return false
  })

  // Sync URL param on mount
  useEffect(() => {
    if (searchParams.get('kamari') === 'true' && !kamariMode) {
      setKamariMode(true)
    }
  }, [searchParams, kamariMode])

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(kamariMode))
  }, [kamariMode])

  const toggleKamariMode = useCallback(() => {
    setKamariMode(prev => !prev)
  }, [])

  return (
    <FeatureFlagContext.Provider value={{ kamariMode, toggleKamariMode }}>
      {children}
    </FeatureFlagContext.Provider>
  )
}
